import type { FC } from "react";
import { useForm } from "@tanstack/react-form";

import { usePatchPatient } from "@/api/patients";
import Edit from "@/assets/icons/edit";
import { FieldDatepicker, FieldInput } from "@/components/form";
import FieldInputPhone from "@/components/form/field-phone";
import DialogCustom from "@/components/modals/dialog-custom";
import SubmitModalFooter from "@/components/modals/submit-modal-footer";
import { Separator } from "@/components/ui/separator";
import { patchAdministrativeInfosSchema } from "@/schemas/patient";
import type { Patient } from "@/types/api/patient";
import { resetForm } from "@/utils/form";

export type UpdateModalProps = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  patient: Patient;
};

const AdministrativeInfosUpdateModal: FC<UpdateModalProps> = ({
  open,
  setOpen,
  patient,
}) => {
  const { mutate } = usePatchPatient(patient.id);

  const {
    birthDate,
    email,
    homePhone,
    mobilePhone,
    job,
    address,
    addressComplement,
    postalCode,
    city,
  } = patient;

  const { Field, handleSubmit } = useForm({
    defaultValues: {
      birthDate,
      email,
      homePhone: homePhone ? homePhone : null,
      mobilePhone: mobilePhone ? mobilePhone : null,
      job: job ? job : null,
      address: address ? address : null,
      addressComplement: addressComplement ? addressComplement : null,
      postalCode: postalCode ? postalCode : null,
      city: city ? city : null,
    },
    onSubmit: async ({ value, formApi }) => {
      const values = patchAdministrativeInfosSchema.parse(value);
      mutate(value, {
        onSuccess: () => {
          setOpen(false);
          resetForm(values, formApi);
        },
      });
    },
  });

  return (
    <DialogCustom
      title="Modifier les informations"
      startIcon={
        <div className="flex p-3 w-12 h-12 justify-center items-center border border-gray-200 rounded-[10px]">
          <Edit className="w-6 h-6" color="black" />
        </div>
      }
      open={open}
      onOpenChange={() => setOpen(false)}
      className="flex flex-col gap-4"
    >
      <Separator />
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 w-full px-6">
        <div className="flex gap-4">
          <FieldDatepicker
            Field={Field}
            schema={patchAdministrativeInfosSchema}
            name="birthDate"
            label="Date de naissance"
            validators={{
              onChange: patchAdministrativeInfosSchema.shape.birthDate,
            }}
          />
          <FieldInput
            Field={Field}
            schema={patchAdministrativeInfosSchema}
            label="Adresse email"
            name="email"
            validators={{
              onChange: patchAdministrativeInfosSchema.shape.email,
            }}
          />
        </div>
        <div className="flex gap-4">
          <FieldInputPhone
            Field={Field}
            schema={patchAdministrativeInfosSchema}
            name="homePhone"
            label="Téléphone fixe"
            placeholder="XX XX XX XX XX"
            validators={{
              onChange: patchAdministrativeInfosSchema.shape.mobilePhone,
            }}
          />
          <FieldInputPhone
            Field={Field}
            schema={patchAdministrativeInfosSchema}
            name="mobilePhone"
            label="Téléphone portable"
            placeholder="XX XX XX XX XX"
            validators={{
              onChange: patchAdministrativeInfosSchema.shape.mobilePhone,
            }}
          />
        </div>
        <FieldInput
          Field={Field}
          schema={patchAdministrativeInfosSchema}
          label="Profession"
          name="job"
          placeholder="Écrire..."
          validators={{
            onChange: patchAdministrativeInfosSchema.shape.job,
          }}
        />
        <div className="flex gap-4">
          <FieldInput
            Field={Field}
            schema={patchAdministrativeInfosSchema}
            label="Adresse"
            name="address"
            placeholder="Adresse"
            validators={{
              onChange: patchAdministrativeInfosSchema.shape.address,
            }}
          />
          <FieldInput
            Field={Field}
            schema={patchAdministrativeInfosSchema}
            label="Complément d'adresse"
            name="addressComplement"
            placeholder="Complément d'adresse"
            validators={{
              onChange: patchAdministrativeInfosSchema.shape.addressComplement,
            }}
          />
        </div>
        <div className="flex gap-4">
          <FieldInput
            Field={Field}
            schema={patchAdministrativeInfosSchema}
            label="Code postal"
            name="postalCode"
            placeholder="Code postal"
            type="number"
            validators={{
              onChange: patchAdministrativeInfosSchema.shape.postalCode,
            }}
          />
          <FieldInput
            Field={Field}
            schema={patchAdministrativeInfosSchema}
            label="Ville"
            name="city"
            placeholder="Ville"
            validators={{
              onChange: patchAdministrativeInfosSchema.shape.city,
            }}
          />
        </div>
        <input type="submit" className="hidden" />
      </form>
      <Separator />
      <SubmitModalFooter setOpen={setOpen} handleSubmit={handleSubmit} />
    </DialogCustom>
  );
};

export default AdministrativeInfosUpdateModal;
